import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * The search filters, stored in the URL rather than in component state.
 *
 * This is the important decision on the search page. Keeping filters in the address bar means the
 * back button steps back through refinements, a filtered search can be sent to someone as a link,
 * a reload does not throw the work away, and the page can be bookmarked. Component state gives none
 * of that, and users expect all of it from a marketplace.
 */
export interface Filters {
  q?: string;
  /**
   * The sentence the buyer typed, kept beside the filters it produced.
   *
   * "audi q8 50000km" becomes three filters and stops being text, but the search box still has to
   * show it: an input that empties itself the moment it works reads as a failure, and there is
   * nothing left to edit and re-submit. It rides in the URL with everything else so a shared link
   * arrives with the box filled in, and it is never sent to the API — the filters are the search.
   */
  qs?: string;
  makeIds: number[];
  modelIds: number[];
  cityIds: number[];
  priceFrom?: number;
  priceTo?: number;
  yearFrom?: number;
  yearTo?: number;
  mileageFrom?: number;
  mileageTo?: number;
  powerKwFrom?: number;
  powerKwTo?: number;
  /**
   * Engine size, which only the clever search sets.
   *
   * There is no control for it in the panel — nobody browses by cubic centimetres — but "gradski
   * auto" and "ekonomično" both mean a small engine, and a filter that is applied has to be one the
   * buyer can see in the chip row and take off again.
   */
  engineCcFrom?: number;
  engineCcTo?: number;
  fuelTypes: number[];
  bodyTypes: number[];
  transmissions: number[];
  driveTypes: number[];
  conditions: number[];
  damageStatuses: number[];
  origins: number[];
  sellerTypes: number[];
  colours: string[];
  equipment: number;
  serviceBook?: boolean;
  firstOwner?: boolean;
  exchangeAccepted?: boolean;
  registeredOnly?: boolean;
  withPhotosOnly?: boolean;
  sort: number;
  page: number;
}

const NUMBER_KEYS = [
  'priceFrom',
  'priceTo',
  'yearFrom',
  'yearTo',
  'mileageFrom',
  'mileageTo',
  'powerKwFrom',
  'powerKwTo',
  'engineCcFrom',
  'engineCcTo',
] as const;

const NUMBER_ARRAY_KEYS = [
  'makeIds',
  'modelIds',
  'cityIds',
  'fuelTypes',
  'bodyTypes',
  'transmissions',
  'driveTypes',
  'conditions',
  'damageStatuses',
  'origins',
  'sellerTypes',
] as const;

const BOOLEAN_KEYS = [
  'serviceBook',
  'firstOwner',
  'exchangeAccepted',
  'registeredOnly',
  'withPhotosOnly',
] as const;

function parseFilters(params: URLSearchParams): Filters {
  const filters = {
    q: params.get('q') ?? undefined,
    qs: params.get('qs') ?? undefined,
    colours: params.getAll('colours'),
    // Equipment is a bitmask, and it can exceed 2^53 only in theory: there are 63 flags but nobody
    // ticks the top ones. Number is safe here and keeps the URL short.
    equipment: Number(params.get('equipment') ?? 0),
    sort: Number(params.get('sort') ?? 0),
    page: Math.max(1, Number(params.get('page') ?? 1)),
  } as Filters;

  for (const key of NUMBER_ARRAY_KEYS) {
    filters[key] = params.getAll(key).map(Number).filter(Number.isFinite);
  }

  for (const key of NUMBER_KEYS) {
    const raw = params.get(key);
    filters[key] = raw === null || raw === '' ? undefined : Number(raw);
  }

  for (const key of BOOLEAN_KEYS) {
    filters[key] = params.get(key) === 'true' ? true : undefined;
  }

  return filters;
}

function toParams(filters: Filters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.q) params.set('q', filters.q);
  if (filters.qs) params.set('qs', filters.qs);

  for (const key of NUMBER_ARRAY_KEYS) {
    for (const value of filters[key]) {
      params.append(key, String(value));
    }
  }

  for (const colour of filters.colours) {
    params.append('colours', colour);
  }

  for (const key of NUMBER_KEYS) {
    const value = filters[key];
    if (value !== undefined && Number.isFinite(value)) {
      params.set(key, String(value));
    }
  }

  for (const key of BOOLEAN_KEYS) {
    if (filters[key]) {
      params.set(key, 'true');
    }
  }

  if (filters.equipment) params.set('equipment', String(filters.equipment));
  if (filters.sort) params.set('sort', String(filters.sort));

  // Page 1 is the default, so leaving it out keeps the common URL clean and makes two links to the
  // same search compare equal.
  if (filters.page > 1) params.set('page', String(filters.page));

  return params;
}

export function useFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);

  const setFilters = useCallback(
    (update: Partial<Filters> | ((current: Filters) => Partial<Filters>)) => {
      const patch = typeof update === 'function' ? update(filters) : update;
      const next = { ...filters, ...patch };

      // Any change other than paging returns to the first page. Staying on page 7 while narrowing
      // a search is how someone lands on an empty page and concludes there are no cars.
      if (!('page' in patch)) {
        next.page = 1;
      }

      setSearchParams(toParams(next), { replace: false });
    },
    [filters, setSearchParams],
  );

  const toggleInArray = useCallback(
    <K extends (typeof NUMBER_ARRAY_KEYS)[number]>(key: K, value: number) => {
      const current = filters[key];
      const next = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];

      setFilters({ [key]: next } as Partial<Filters>);
    },
    [filters, setFilters],
  );

  const toggleEquipment = useCallback(
    (flag: number) => {
      setFilters({
        equipment: filters.equipment & flag ? filters.equipment & ~flag : filters.equipment | flag,
      });
    },
    [filters, setFilters],
  );

  const clear = useCallback(() => setSearchParams(new URLSearchParams()), [setSearchParams]);

  /**
   * Swaps the entire search for a new one.
   *
   * The search box is the one control that starts again rather than refining: a fresh sentence
   * describes a different car, and quietly keeping the gearbox from the last one is how someone
   * ends up staring at an empty result set with no idea which of the filters is to blame.
   */
  const replaceAll = useCallback(
    (params: URLSearchParams) => {
      const next = new URLSearchParams(params);
      const sort = searchParams.get('sort');

      // Sort order survives, because it is a preference rather than part of the search: someone who
      // asked for cheapest-first meant it for whatever they look at next too.
      if (sort !== null && !next.has('sort')) {
        next.set('sort', sort);
      }

      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  /** Drives the "clear filters" button and the count badge on the mobile filter trigger. */
  const activeCount = useMemo(() => {
    let count = 0;

    for (const key of NUMBER_ARRAY_KEYS) count += filters[key].length;
    for (const key of NUMBER_KEYS) if (filters[key] !== undefined) count += 1;
    for (const key of BOOLEAN_KEYS) if (filters[key]) count += 1;

    count += filters.colours.length;
    if (filters.equipment) {
      // Counted as one filter however many flags are set: "oprema" is one row in the UI.
      count += 1;
    }
    if (filters.q) count += 1;

    return count;
  }, [filters]);

  return {
    filters,
    setFilters,
    replaceAll,
    toggleInArray,
    toggleEquipment,
    clear,
    activeCount,
    searchParams,
  };
}
